import { TrackingEvent, TrackingEventTypes } from '@cinch-nx/shared-analytics'
import { LoopingCarousel } from '@cinch-nx/shared-ui'
import { StoryblokRendererV2 as StoryblokRenderer } from '@cinch-nx/storyblok-utils'
import { SbBlokData } from '@storyblok/react'
import { useRouter } from 'next/router'
import { useRef } from 'react'

import { HeroCard } from '../hero-card'
import styles from './hero-carousel.module.css'

const SESSION_STORAGE_KEY = 'hero-carousel-tracked-impressions'

function getTrackedImpressions(): Set<string> {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)
    return stored ? new Set(JSON.parse(stored)) : new Set()
  } catch {
    return new Set()
  }
}

function hasAnalyticsCookieConsent(): boolean {
  try {
    const cookie = document.cookie.match('(^|;)\\s*cp\\s*=\\s*([^;]+)')?.pop()
    return cookie ? JSON.parse(cookie)?.analytics === true : false
  } catch {
    return false
  }
}

function markImpressionTracked(slideId: string): void {
  if (!hasAnalyticsCookieConsent()) return

  try {
    const tracked = getTrackedImpressions()
    tracked.add(slideId)
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify([...tracked]))
  } catch {
    console.warn('The sessionStorage may be unavailable.')
  }
}

interface HeroCarouselProps {
  slides: SbBlokData[]
  slideSpeedInSeconds?: number
  shouldLoop?: boolean
}

const heroCarouselStoryblokComponents = {
  HomeHeroCard: HeroCard,
}

function getSlideTrackingEvents(slide: SbBlokData, index: number) {
  const firstCta = Array.isArray(slide.cta) ? slide.cta[0] : undefined
  const ctaText = firstCta?.label
  const ctaDestination = firstCta?.url
  const position = index + 1

  const onClickEvent: TrackingEvent = {
    eventName: 'Carousel card click',
    type: TrackingEventTypes.ADOBE,
    data: {
      event: {
        name: 'carousel_click',
        category: 'homepage-hero-carousel',
        label: slide.campaignName as string,
        action: 'click',
        type: 'hero-card',
        value: String(position),
        carousel: {
          cardHeadline: slide.heading as string,
          position,
          ...(ctaText && { ctaText }),
          ...(ctaDestination && { ctaDestination }),
        },
      },
    },
  }

  const onLoadEvent: TrackingEvent = {
    eventName: 'Carousel card impression',
    type: TrackingEventTypes.ADOBE,
    data: {
      event: {
        ...onClickEvent.data.event,
        name: 'carousel_impression',
        action: 'load',
      },
    },
  }

  const linkClickTrackingEvent: TrackingEvent = {
    ...onClickEvent,
    data: {
      event: {
        ...onClickEvent.data.event,
        type: 'hero-card-link',
      },
    },
  }

  return {
    trackingEvents: { onClick: onClickEvent, onLoad: onLoadEvent },
    linkClickTrackingEvent,
    ctaDestination,
  }
}

export default function HeroCarousel({
  slides = [],
  slideSpeedInSeconds = 2,
  shouldLoop = true,
}: HeroCarouselProps) {
  const router = useRouter()
  const trackedImpressionsRef = useRef(getTrackedImpressions())

  if (slides.length === 0) {
    return null
  }

  return (
    <LoopingCarousel
      slideSpeedInSeconds={slideSpeedInSeconds}
      shouldLoop={shouldLoop}
      className={{
        carousel: styles.carousel,
        carouselSlide: styles.carouselSlide,
        controlsWrapper: styles.controlsWrapper,
        customPagination: styles.customPagination,
      }}
    >
      {slides.map((slide, index) => {
        const { trackingEvents, linkClickTrackingEvent, ctaDestination } =
          getSlideTrackingEvents(slide, index)

        const slideId = (slide._uid as string) || `slide-${index}`
        const isImpressionAlreadyTracked =
          trackedImpressionsRef.current.has(slideId)

        const sessionAwareTrackingEvents = {
          ...trackingEvents,
          onLoad: isImpressionAlreadyTracked
            ? undefined
            : trackingEvents.onLoad,
        }

        const heading = (slide.heading as string) || `Slide ${index + 1}`
        const ariaLabel = `${heading}, slide ${index + 1} of ${slides.length}`
        return (
          <LoopingCarousel.Slide
            key={slide._uid}
            trackingEvents={sessionAwareTrackingEvents}
            ariaLabel={ariaLabel}
            onImpression={() => {
              trackedImpressionsRef.current.add(slideId)
              markImpressionTracked(slideId)
            }}
            onClick={async () => {
              if (ctaDestination) {
                await router.push(ctaDestination)
              }
            }}
          >
            <StoryblokRenderer
              block={slide}
              components={heroCarouselStoryblokComponents}
              props={{ linkClickTrackingEvent }}
            />
          </LoopingCarousel.Slide>
        )
      })}
    </LoopingCarousel>
  )
}
